import type { Department } from "../contexts/AuthProvider";
import type { NurseRole } from "./models";

export type ShiftTimeOptions = {
	partialBlockShifts: number[];
	fullShiftTimes: [start: number, end: number][];
};

/** The subset of Department this resolver needs, so callers can pass partial fixtures in tests. */
export type ShiftTimeDepartment = Pick<Department, "partial_block_shifts" | "full_shift_times" | "role_groups"> & Partial<Pick<Department, "jobShiftTimes">>;

export type ResolveShiftTimeOptionsArgs = {
	department: ShiftTimeDepartment;
	jobManagementEnabled?: boolean;
	/** The job the shift is being scheduled under. Optional — callers with no job fall back to role. */
	jobId?: string;
	role?: `${NurseRole}` | "default";
	/** When set, full shift times are unioned across every role in these groups (role-mode concept). */
	roleGroups?: string[];
};

const EMPTY_OPTIONS: ShiftTimeOptions = { partialBlockShifts: [], fullShiftTimes: [] };

/**
 * Resolve the shift-length options for a picker (DAT-580).
 *
 * Precedence, applied to each of the two lists independently because a job may define full shift
 * times without partial lengths (the legacy role-keyed maps are already independent this way):
 *
 *   1. `roleGroups` — union across the group's roles. Deliberately NOT job-aware: `role_groups` is
 *      itself role-keyed and there is no role→job map on the frontend.
 *   2. the department's per-job row, when the department is job-managed and a `jobId` is known.
 *      Matched on row PRESENCE, not list length — an explicitly-cleared job must be able to
 *      suppress the default, exactly as an explicitly-cleared role key does.
 *   3. the role key on the legacy map. Covers every role-mode department, plus job-mode departments
 *      in the window between this code shipping and the backfill running.
 *   4. the `default` bucket.
 *
 * `jobManagementEnabled` is supplied by the caller from FeatureFlagsProvider, which evaluates for the
 * chosen department. If `EditTimePopover`'s currently-unused `specificDepartmentId` prop is ever
 * wired up, that caller will need a per-department value instead — the flag would otherwise answer
 * for the wrong department, since one user can hold departments in both modes.
 */
export function resolveShiftTimeOptions({ department, jobManagementEnabled, jobId, role, roleGroups }: ResolveShiftTimeOptionsArgs): ShiftTimeOptions {
	const jobRow = jobManagementEnabled && jobId ? department.jobShiftTimes?.find((row) => row.jobId === jobId) : undefined;

	return {
		partialBlockShifts: resolvePartialBlockShifts(department, jobRow, role),
		fullShiftTimes: resolveFullShiftTimes(department, jobRow, role, roleGroups),
	};
}

function resolvePartialBlockShifts(
	department: ShiftTimeDepartment,
	jobRow: { partialBlockShifts: number[] } | undefined,
	role: `${NurseRole}` | "default" | undefined,
): number[] {
	const hours = jobRow ? jobRow.partialBlockShifts : ((role && department.partial_block_shifts[role]) ?? department.partial_block_shifts.default);
	// Ascending so the chips read low-to-high regardless of the order they were entered in Settings.
	return hours ? [...hours].sort((a, b) => a - b) : EMPTY_OPTIONS.partialBlockShifts;
}

function resolveFullShiftTimes(
	department: ShiftTimeDepartment,
	jobRow: { fullShiftTimes: [start: number, end: number][] } | undefined,
	role: `${NurseRole}` | "default" | undefined,
	roleGroups: string[] | undefined,
): [start: number, end: number][] {
	if (roleGroups?.length) return unionFullShiftTimesForRoleGroups(department, roleGroups);
	if (jobRow) return jobRow.fullShiftTimes;
	return (role && department.full_shift_times[role]) ?? department.full_shift_times.default ?? EMPTY_OPTIONS.fullShiftTimes;
}

/** Dedupe by the start-end pair so a time shared by two roles in the group renders once. */
function unionFullShiftTimesForRoleGroups(department: ShiftTimeDepartment, roleGroups: string[]): [start: number, end: number][] {
	const rolesInGroups = roleGroups.flatMap((group) => department.role_groups[group] ?? []);
	const byTimePair = new Map<string, [start: number, end: number]>();
	for (const groupRole of rolesInGroups) {
		for (const [start, end] of department.full_shift_times[groupRole as NurseRole] ?? []) {
			byTimePair.set(`${start}-${end}`, [start, end]);
		}
	}
	return [...byTimePair.values()];
}
